import type { EventStatus, OperationEvent, OperationsSnapshot } from "../../domain/operations";

export const eventLifecycleFilters = [
  { id: "inquiry", label: "Inquiry" },
  { id: "confirmed", label: "Confirmed" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
  { id: "all", label: "All" },
] as const;

export type EventLifecycleFilter = (typeof eventLifecycleFilters)[number]["id"];

export const defaultEventLifecycleFilter: EventLifecycleFilter = "confirmed";

export function isEventLifecycleFilter(value: string | null): value is EventLifecycleFilter {
  return eventLifecycleFilters.some((item) => item.id === value);
}

export function resolveEventLifecycleFilter(value: string | null): EventLifecycleFilter {
  return isEventLifecycleFilter(value) ? value : defaultEventLifecycleFilter;
}

/** Confirmed Events actively executing in Operations. */
export function isInProgressEvent(event: OperationEvent, snapshot: OperationsSnapshot): boolean {
  if (event.status !== "confirmed") return false;
  const tasks = snapshot.tasks.filter((task) => task.eventId === event.id);
  if (tasks.some((task) => task.status === "in_progress")) return true;
  return /today/i.test(event.dateLabel) && tasks.some((task) => task.status !== "complete");
}

export function eventMatchesLifecycle(
  event: OperationEvent,
  filter: EventLifecycleFilter,
  snapshot: OperationsSnapshot,
): boolean {
  if (filter === "all") return true;
  if (filter === "in_progress") return isInProgressEvent(event, snapshot);
  if (filter === "confirmed") return event.status === "confirmed" && !isInProgressEvent(event, snapshot);
  return event.status === filter;
}

export function lifecycleCounts(snapshot: OperationsSnapshot): Record<EventLifecycleFilter, number> {
  const counts: Record<EventLifecycleFilter, number> = {
    inquiry: 0,
    confirmed: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    all: snapshot.events.length,
  };

  for (const event of snapshot.events) {
    if (event.status === "inquiry") counts.inquiry += 1;
    if (event.status === "completed") counts.completed += 1;
    if (event.status === "cancelled") counts.cancelled += 1;
    if (isInProgressEvent(event, snapshot)) counts.in_progress += 1;
    else if (event.status === "confirmed") counts.confirmed += 1;
  }

  return counts;
}

export function lifecycleStatusParam(filter: EventLifecycleFilter): string {
  return filter === defaultEventLifecycleFilter ? "confirmed" : filter;
}
